import { createContext, useContext } from 'react';
import type { FunctionComponent } from 'react';

import useJobNotificationHub from './useJobNotificationHub';
import type { AddJobFn } from './types';

//  The context is what we need to make available to the rest of the application.
//  Since the handling of jobs and notifications all happens here, the only thing which
//  we need to expose is the onAddJob function (this is returned by the useJobNotificationHub hook).
export const JobNotificationContext = createContext<AddJobFn>(() => {});

/**
 * Hook to make it easier to consumer the JobNotificationHub context.
 *
 * @example
 * const MyComponent = () => {
 *   const addJob = useAddJobToNotificationQueue();
 *   const someAsyncFunctionIdk = async () => {
 *     // ...do something, ususally a request which returns a job token...
 *     addJob(response.task.jobToken, response.task.task);
 *   };
 * };
 */
export const useAddJobToNotificationQueue = () => useContext<AddJobFn>(JobNotificationContext);

/**
 * The state is all managed in the `useJobNotificationHub` hook.
 */
const JobNotificationProvider: FunctionComponent = ({ children }) => {
  const onAddJob = useJobNotificationHub();
  return (
    <JobNotificationContext.Provider value={onAddJob}>
      {children}
    </JobNotificationContext.Provider>
  );
};

export default JobNotificationProvider;
